// Parses — and then destroys — the launch parameters the host app hands the
// WebView in the URL FRAGMENT.
//
// Why the fragment and not the query string: a fragment is never sent to a
// server, never written to an access log, and never leaks in a `Referer`
// header. The one-time exchange code is a bearer credential for ~60 seconds;
// putting it in `?code=` would persist it in every hop's logs.
//
// Why we then erase it: the fragment survives in `location.hash` for the life
// of the document, which means any later-loaded script, any devtools session,
// and any screenshot of the URL bar can read a code we have already used. We
// read it once, hand it to the exchange, and `history.replaceState` it away.

import { defaultPartner } from '../config';

/** The launch contract version this build understands. See PARTNER_BRIDGE_CONTRACT.md. */
export const SUPPORTED_CONTRACT_VERSION = 1;

export interface LaunchParams {
  /** Contract version the host claims to speak. */
  readonly v: number;
  /**
   * Which partner is hosting this embed.
   *
   * A launch field rather than a build-time constant, so ONE deployment of
   * this renderer serves every partner. That is what makes the embed scale:
   * a new host is a registry row on the server plus a launch URL, with no
   * fork of this bundle and no second deploy to keep in step.
   *
   * Falls back to VITE_DEFAULT_PARTNER for a host that predates the field.
   */
  readonly partner: string;
  /** One-time partner exchange code. Single use, ~60s TTL. */
  readonly code: string | null;
  /** Opaque trip id, when the rider launched from inside a trip. */
  readonly trip: string | null;
  /** Opaque route id. */
  readonly route: string | null;
  /** BCP-47-ish language tag chosen by the host app. */
  readonly lang: string;
  /** Host-reported safe-area insets, in CSS pixels. */
  readonly safeTop: number;
  readonly safeBottom: number;
}

/**
 * Reads the launch params out of `window.location.hash`, then clears the hash.
 *
 * Never throws: a malformed or absent fragment yields nulls, and the caller
 * renders the "couldn't start" state rather than a stack trace inside someone
 * else's app.
 */
export function readLaunchParams(loc: Location = window.location): LaunchParams {
  const raw = loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash;
  const p = new URLSearchParams(raw);

  const params: LaunchParams = {
    v: toInt(p.get('v'), SUPPORTED_CONTRACT_VERSION),
    partner: nonEmpty(p.get('partner')) ?? defaultPartner,
    code: nonEmpty(p.get('code')),
    trip: nonEmpty(p.get('trip')),
    route: nonEmpty(p.get('route')),
    lang: nonEmpty(p.get('lang')) ?? 'en',
    safeTop: toInt(p.get('safe_top'), 0),
    safeBottom: toInt(p.get('safe_bottom'), 0),
  };

  scrubFragment();
  return params;
}

/**
 * Removes the fragment without adding a history entry and without reloading.
 * Falls back to assigning `location.hash` on the (unlikely) engine that has no
 * History API — a WebView that old will not run the rest of this bundle
 * either, so the fallback exists to avoid throwing, not to work well.
 */
function scrubFragment(): void {
  try {
    const { pathname, search } = window.location;
    window.history.replaceState(null, '', pathname + search);
  } catch {
    try {
      window.location.hash = '';
    } catch {
      /* nothing left to try; the code is short-lived and single-use anyway. */
    }
  }
}

/**
 * Applies the host's safe-area insets as CSS custom properties.
 *
 * The WebView is not full-bleed inside the host's Scaffold, so
 * `env(safe-area-inset-*)` reports zero even on a notched device — the host
 * knows the real numbers and passes them in. We still `max()` against `env()`
 * so a future full-bleed host, or a browser opened directly for debugging,
 * gets the right answer without a contract change.
 */
export function applySafeArea(params: LaunchParams, root: HTMLElement = document.documentElement): void {
  root.style.setProperty('--safe-top', `${params.safeTop}px`);
  root.style.setProperty('--safe-bottom', `${params.safeBottom}px`);
}

function nonEmpty(v: string | null): string | null {
  const t = v?.trim();
  return t ? t : null;
}

function toInt(v: string | null, fallback: number): number {
  const n = Number.parseInt(v ?? '', 10);
  return Number.isFinite(n) ? n : fallback;
}
